import * as React from "react";
import {
  Box,
  Typography,
  Stack,
  Paper,
  Checkbox,
  Button,
  Alert,
  Snackbar,
} from "@mui/material";
import { useI18n } from "../../i18n/I18nProvider";
import { supabase } from "../../logic/client";
import { useWorkshopBlocker, FLOW } from "../../logic/useWorkshopBlocker";

type Option = {
  key: string;
  label_en: string;
  label_de: string;
};

const OPTIONS: Option[] = [
  { key: "easy", label_en: "That was easy", label_de: "Das war einfach" },
  { key: "exhausted", label_en: "I got exhausted faster than expected", label_de: "Ich war schneller erschöpft als gedacht" },
  { key: "longer", label_en: "I could have kept going longer", label_de: "Ich hätte das länger durchgehalten" },
  { key: "need_help", label_en: "I would need help from others", label_de: "Ich bräuchte Hilfe von anderen" },
];

const STORAGE_PREFIX = "evacuationTaskReview";
const CACHE_TTL = 1000 * 60 * 30; // 30 min session

export default function EvacuationTaskReview(props: { handlePageChange: (p: string) => void }) {
  const { handlePageChange } = props;
  const blocker = useWorkshopBlocker();
  const { lang } = useI18n();
  const isDE = lang === "de";
  const [notice, setNotice] = React.useState<string | null>(null);
  const [selected, setSelected] = React.useState<string[]>([]);
  const [submitted, setSubmitted] = React.useState(false);

  const [workshopId, setWorkshopId] = React.useState<string | null>(null);
  const [employeeId, setEmployeeId] = React.useState<string | null>(null);

  /* ---------------- BLOCKER ---------------- */
  const myIndex = FLOW.indexOf("EvacuationTaskReview");
  const blockerIndex = FLOW.indexOf(blocker);
  const canContinue = blockerIndex > myIndex;

  function handleContinue() {
    if (canContinue) {
      handlePageChange(FLOW[myIndex + 1]);
      return;
    }
    setNotice("ENG / DE");
  }

  const loadLocal = React.useCallback(
    (workshopId: string, employeeId: string, backendSelected: string[]) => {
      const key = `${STORAGE_PREFIX}_${workshopId}_${employeeId}`;
      const saved = localStorage.getItem(key);

      if (backendSelected.length === 0) {
        localStorage.removeItem(key);
        setSelected([]);
        setSubmitted(false);
        return;
      }

      if (!saved) {
        setSelected(backendSelected);
        setSubmitted(true);
        return;
      }

      try {
        const parsed = JSON.parse(saved);

        if (
          !parsed ||
          !Array.isArray(parsed.selected) ||
          typeof parsed.timestamp !== "number"
        ) {
          localStorage.removeItem(key);
          setSelected(backendSelected);
          setSubmitted(true);
          return;
        }

        if (Date.now() - parsed.timestamp > CACHE_TTL) {
          localStorage.removeItem(key);
          setSelected(backendSelected);
          setSubmitted(true);
          return;
        }

        setSelected(parsed.selected);
        setSubmitted(true);
      } catch (e) {
        console.error("LOCAL PARSE ERROR", e);
        localStorage.removeItem(key);
        setSelected(backendSelected);
        setSubmitted(true);
      }
    },
    []
  );

  const loadContext = React.useCallback(async () => {
    const { data: userRes } = await supabase.auth.getUser();

    const { data: emp } = await supabase
      .from("employees")
      .select("id")
      .eq("auth_user_id", userRes?.user?.id)
      .single();

    const { data: workshop } = await supabase
      .from("workshops")
      .select("id")
      .eq("is_active", true)
      .single();

    if (!emp || !workshop) return;

    setEmployeeId(emp.id);
    setWorkshopId(workshop.id);

    const { data: existingAnswers, error } = await supabase
      .from("workshop_participant_reflection")
      .select("answer_key")
      .eq("workshop_id", workshop.id)
      .eq("employee_id", emp.id);

    if (error) {
      console.error("LOAD REFLECTION ERROR:", error);
      return;
    }

    const backendSelected = existingAnswers?.map((row) => row.answer_key) ?? [];

    loadLocal(workshop.id, emp.id, backendSelected);
  }, [loadLocal]);

  React.useEffect(() => {
    loadContext();
  }, [loadContext]);

  function toggle(key: string) {
    if (submitted) return;

    setSelected((prev) =>
      prev.includes(key)
        ? prev.filter((k) => k !== key)
        : [...prev, key]
    );
  }

  /* ---------------- SUBMIT ---------------- */

  async function handleSubmit() {
    if (!workshopId || !employeeId || selected.length === 0) return;

    const localKey = `${STORAGE_PREFIX}_${workshopId}_${employeeId}`;

    localStorage.setItem(
      localKey,
      JSON.stringify({
        selected,
        timestamp: Date.now(),
      })
    );

    await supabase
      .from("workshop_participant_reflection")
      .delete()
      .eq("workshop_id", workshopId)
      .eq("employee_id", employeeId);

    const rows = selected.map((key) => ({
      workshop_id: workshopId,
      employee_id: employeeId,
      answer_key: key,
    }));

    const { error } = await supabase
      .from("workshop_participant_reflection")
      .insert(rows);

    if (error) {
      console.error("INSERT ERROR:", error);
      return;
    }

    setSubmitted(true);
  }

  function handleRedo() {
    if (workshopId && employeeId) {
      localStorage.removeItem(`${STORAGE_PREFIX}_${workshopId}_${employeeId}`);
    }

    setSelected([]);
    setSubmitted(false);
  }

  return (
    <Box
      sx={{
        minHeight: "100vh",
        px: 2,
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        backgroundImage: `
          linear-gradient(
            rgba(8, 8, 8, 0.78),
            rgba(8, 8, 8, 0.82)
          ),
          url("/scenarioBgs/evacuation.png")
        `,
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
        backgroundAttachment: { xs: "scroll", md: "fixed" },
        py: { xs: 3, sm: 5 },
      }}
    >
      <Paper
        sx={{
          width: "min(900px, 100%)",
          maxWidth: 900,
          p: { xs: 2.5, sm: 4, md: 5 },
          borderRadius: 5,
          background: "linear-gradient(180deg, #311f1f, #221515)",
        }}
      >
        <Stack spacing={{ xs: 3, sm: 4 }}>
          <Typography sx={{ color: "#fff", fontWeight: 800, fontSize: { xs: "1.3rem", sm: "1.5rem", md: "1.6rem" } }}>
            {isDE ? "Was hast du gerade erlebt?" : "What did you just experience?"}
          </Typography>

          {!submitted ? (
            <>
              <Stack>
                {OPTIONS.map((opt) => {
                  const checked = selected.includes(opt.key);

                  return (
                    <Box
                      key={opt.key}
                      onClick={() => toggle(opt.key)}
                      sx={{
                        border: checked
                          ? "1px solid #ff4d4d"
                          : "1px solid rgba(255,255,255,0.2)",
                        borderRadius: 2,
                        mb: 1,
                        px: 2,
                        py: 1.2,
                        display: "flex",
                        alignItems: "center",
                        gap: 2,
                        cursor: "pointer",
                        background: checked
                          ? "rgba(255,77,77,0.10)"
                          : "rgba(255,255,255,0.03)",
                      }}
                    >
                      <Checkbox
                        checked={checked}
                        sx={{
                          color: "#888",
                          "&.Mui-checked": { color: "#ff4d4d" },
                        }}
                      />

                      <Typography sx={{ color: "#fff" }}>
                        {isDE ? opt.label_de : opt.label_en}
                      </Typography>
                    </Box>
                  );
                })}
              </Stack>

              <Button
                variant="contained"
                onClick={handleSubmit}
                disabled={selected.length === 0}
                sx={{
                  background: "linear-gradient(90deg, #b50d20, #d6293e)",
                  fontWeight: 700,
                  "&.Mui-disabled": {
                    background: "#777",
                    color: "rgba(255,255,255,0.75)",
                  },
                }}
              >
                {isDE ? "Antwort speichern" : "Submit answer"}
              </Button>
            </>
            ) : (
              <Stack spacing={2}>
                <Box
                  sx={{
                    p: 2.5,
                    borderRadius: 3,
                    background: "rgba(0,0,0,0.35)",
                    border: "1px solid rgba(255,255,255,0.16)",
                    textAlign: "center",
                  }}
                >
                  <Typography
                    sx={{
                      color: "#fff",
                      fontSize: "1.1rem",
                      fontWeight: 800,
                      lineHeight: 1.6,
                    }}
                  >
                    {isDE
                      ? "Danke für deine Einschätzung. Diese hilft dir, deine eigenen Grenzen besser zu verstehen."
                      : "Thank you for your reflection. This helps you better understand your own limits."}
                  </Typography>
                </Box>

                <Button
                  variant="outlined"
                  onClick={handleRedo}
                  sx={{
                    color: "#fff",
                    borderColor: "rgba(255,255,255,0.4)",
                    fontWeight: 700,
                    "&:hover": {
                      borderColor: "#fff",
                      background: "rgba(255,255,255,0.05)",
                    },
                  }}
                >
                  {isDE ? "Antwort ändern" : "Change answer"}
                </Button>

                <Button
                  variant="contained"
                  onClick={handleContinue}
                  sx={{
                    mt: 1,
                    borderRadius: 999,
                    textTransform: "none",
                    fontWeight: 800,
                    px: 3,
                    bgcolor: canContinue ? "#ffffff" : "rgba(255,255,255,0.35)",
                    color: canContinue ? "#111827" : "rgba(255,255,255,0.8)",
                    "&:hover": {
                      bgcolor: canContinue ? "#f3f4f6" : "rgba(255,255,255,0.35)",
                    },
                  }}
                >
                  {isDE ? "Weiter" : "Continue"}
                </Button>
              </Stack>
            )}
        </Stack>
      </Paper>

      <Snackbar
        open={Boolean(notice)}
        autoHideDuration={5000}
        onClose={() => setNotice(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert onClose={() => setNotice(null)} severity="info" sx={{ width: "100%", fontWeight: 700 }}>
          {isDE
            ? "Die Workshop-Leitung wird diesen Schritt freigeben, oder bitte beantworte die Frage vollständig und sende sie zuerst ab."
            : "The workshop holder will enable this step, or please complete and submit your answer first."}
        </Alert>
      </Snackbar>
    </Box>
  );
}